/**
 * **The two notes under the agenda** — what needs the trainer's decision, and which starts clients
 * can still take this week.
 *
 * "Wymaga Twojej decyzji" lists only what the data states outright: a consultation whose hour has
 * passed and whose outcome nobody recorded (the mobile sheet's `consultAwaiting`). A workout the
 * server calls missed is NOT listed — whether it needs a verdict depends on whether anything was
 * staged behind it, which is a question for the session panel, not for a note.
 *
 * "Wolne terminy dla klientów" is the first free start of each remaining day of the shown week, out
 * of `GET /trainer-availability/slots` — the slots R5 derives, so this repeats no rule of its own.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import type { TrainWithMeModulesBookingDtosResponsesBookingSlotsResponse as BookingSlotsResponse } from '~/shared/api/generated/model';
import { parseInstant } from '~/shared/lib/calendarDate';

import { awaitsVerdict, isLiveAt, type BookingSession } from './bookingSession';
import type { CivilDay } from './trainerClock';

/** Sessions whose outcome the trainer still owes, oldest first. */
export function awaitingDecision(
  sessions: readonly BookingSession[],
  nowMs: number,
): BookingSession[] {
  return sessions
    .filter((session) => awaitsVerdict(session, nowMs) && !isLiveAt(session, nowMs))
    .sort((a, b) => (a.startMs ?? 0) - (b.startMs ?? 0));
}

export interface FreeStart {
  readonly day: CivilDay;
  readonly startMs: number;
}

/**
 * The first start still ahead on each day from `from` to `last`. The slot read already drops what
 * the minimum notice forbids; `nowMs` guards the minutes between that read and this render.
 */
export function firstFreeStarts(
  slots: BookingSlotsResponse,
  from: CivilDay,
  last: CivilDay,
  nowMs: number,
): FreeStart[] {
  return (slots.days ?? []).flatMap((entry) => {
    const day = entry.date.slice(0, 10);
    if (day < from || day > last) return [];
    const first = (entry.starts ?? [])
      .map((start) => parseInstant(start.start))
      .filter((ms): ms is number => ms !== null && ms > nowMs)
      .sort((a, b) => a - b)[0];
    return first === undefined ? [] : [{ day, startMs: first }];
  }).sort((a, b) => a.startMs - b.startMs);
}
